import {
  GoogleMap,
  GoogleMapProps,
  MarkerClustererF,
  MarkerF,
  PolylineF,
  InfoWindowF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { Box, Typography } from "@mui/material";
import { CSSProperties, ReactNode, useState } from "react";

import mapStyles from "./mapStyles";
import { GOOGLE_MAPS_API_KEY } from "../../../config/keys";
import ExtensionUIComponentContainer from "../../extensions/ExtensionUIComponent";
import { ExtensionUiComponentType } from "../../../graphql/generated";
import { testIds } from "./Map.testIds";

const containerStyle = {
  width: "100%",
  height: "400px",
};

// Serializable info window content schema — used by extension maps
export type InfoWindowContentItem =
  | { type: "text"; value: string; bold?: boolean; size?: number }
  | { type: "row"; items: InfoWindowContentItem[] }
  | { type: "stack"; items: InfoWindowContentItem[] }
  | { type: "divider" }
  | { type: "link"; label: string; href: string }
  | { type: "chip"; label: string };

export type InfoWindowContent = InfoWindowContentItem[];

// Types for map elements
export interface MarkerData {
  position: google.maps.LatLng | google.maps.LatLngLiteral;
  options?: google.maps.MarkerOptions;
  onClick?: () => void;
  infoWindow?: {
    /** Rich JSX content rendered by the default Google Maps fallback */
    content: ReactNode;
    /** Serializable content rendered by extension maps (e.g. Trimble Maps) */
    extensionContent?: InfoWindowContent;
    onCloseClick?: () => void;
    position?: google.maps.LatLng | google.maps.LatLngLiteral;
  };
}

export interface PolylineData {
  path?: google.maps.LatLng[] | google.maps.LatLngLiteral[];
  polylineString?: string;
  options?: google.maps.PolylineOptions;
}

export interface InfoWindowData {
  position: google.maps.LatLng | google.maps.LatLngLiteral;
  /** Rich JSX content rendered by the default Google Maps fallback */
  content: ReactNode;
  /** Serializable content rendered by extension maps */
  extensionContent?: InfoWindowContent;
  onCloseClick?: () => void;
}

export interface MapProps extends GoogleMapProps {
  containerStyle?: CSSProperties;
  onLoad?: (map: google.maps.Map | null) => void;
  markers?: MarkerData[];
  polylines?: PolylineData[];
  infoWindows?: InfoWindowData[];
  /** Enables marker clustering in the default Google Maps renderer */
  clustered?: boolean;
  /** When true, always renders the standard Google Maps regardless of extension maps */
  useStandardMap?: boolean;
}

/** Renders a serializable InfoWindowContent schema into React nodes — used by extension maps */
export function renderInfoWindowContent(
  items: InfoWindowContent
): React.ReactNode {
  return items.map((item, i) => {
    switch (item.type) {
      case "text":
        return (
          <Typography
            key={i}
            sx={{
              fontSize: item.size ?? 13,
              fontWeight: item.bold ? 600 : 400,
            }}
          >
            {item.value}
          </Typography>
        );
      case "row":
        return (
          <Box key={i} sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            {renderInfoWindowContent(item.items)}
          </Box>
        );
      case "stack":
        return (
          <Box
            key={i}
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            {renderInfoWindowContent(item.items)}
          </Box>
        );
      case "divider":
        return (
          <Box
            key={i}
            sx={{ borderTop: "1px solid #eee", my: 0.5, width: "100%" }}
          />
        );
      case "link":
        return (
          <a data-testid={`${testIds.mapInfoWindow}-${String(
              item.href || item.label
            )}`} key={i} href={item.href} target="_blank" rel="noreferrer">
            {item.label}
          </a>
        );
      case "chip":
        return (
          <Box
            key={i}
            sx={{
              display: "inline-block",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              border: "1px solid",
              fontSize: 12,
            }}
          >
            {item.label}
          </Box>
        );
      default:
        return null;
    }
  });
}

function Map({
  containerStyle: customContainerStyle,
  onLoad,
  markers = [],
  polylines = [],
  infoWindows = [],
  children,
  useStandardMap = false,
  ...googleMapProps
}: MapProps) {
  // Strip non-serializable fields (functions, class instances, ReactNode)
  const serializableMarkers = markers.map(({ infoWindow, onClick, options, ...marker }) => {
    const iconUrl =
      options?.icon && typeof options.icon === "object" && "url" in options.icon
        ? (options.icon as google.maps.Icon).url
        : undefined;
    // Make icon URL absolute so the extension iframe can load it cross-origin
    const absoluteIconUrl =
      iconUrl && iconUrl.startsWith("/")
        ? `${window.location.origin}${iconUrl}`
        : iconUrl;
    return {
      ...marker,
      ...(options && {
        options: {
          ...options,
          icon: absoluteIconUrl ? { url: absoluteIconUrl } : options.icon,
        },
      }),
      ...(infoWindow && {
        infoWindow: {
          extensionContent: infoWindow.extensionContent,
          position: infoWindow.position,
        },
      }),
    };
  });
  const serializableInfoWindows = infoWindows.map(
    ({ content: _content, onCloseClick: _onCloseClick, ...iw }) => iw
  );

  const { onLoad: _onLoad, children: _children, ...serializableGoogleMapProps } = googleMapProps as any;

  const componentProps = {
    zoom: googleMapProps.zoom || 10,
    center: googleMapProps.center || {
      lat: 39.90544474495378,
      lng: -82.91304185748207,
    },
    markers: serializableMarkers,
    polylines: polylines.filter((polyline) => !!polyline.polylineString),
    infoWindows: serializableInfoWindows,
    ...serializableGoogleMapProps,
  };

  // Original props passed to DefaultMap — preserves ReactNode content and callbacks
  const fallbackProps: MapProps = {
    containerStyle: customContainerStyle,
    onLoad,
    markers,
    polylines,
    infoWindows,
    children,
    ...googleMapProps,
  };

  if (useStandardMap) {
    return <DefaultMap {...fallbackProps} />;
  }

  return (
    <div style={{ height: "100%" }}>
      <ExtensionUIComponentContainer
        type={ExtensionUiComponentType.Map}
        componentProps={componentProps}
        fallbackProps={fallbackProps}
        onLoad={() => {
          onLoad?.(null);
        }}
        fallback={DefaultMap}
      />
    </div>
  );
}

const MarkerWithInfoWindow = ({
  marker,
  clusterer,
}: {
  marker: MarkerData;
  clusterer?: any;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <MarkerF
      position={marker.position}
      options={marker.options}
      clusterer={clusterer}
      onClick={() => {
        marker.onClick?.();
        if (marker.infoWindow) setIsOpen(true);
      }}
    >
      {marker.infoWindow && isOpen && (
        <InfoWindowF
          position={marker.infoWindow.position || marker.position}
          onCloseClick={() => {
            marker.infoWindow?.onCloseClick?.();
            setIsOpen(false);
          }}
        >
          <Box>{marker.infoWindow.content}</Box>
        </InfoWindowF>
      )}
    </MarkerF>
  );
};

const DefaultMap = ({
  containerStyle: customContainerStyle,
  onLoad,
  markers = [],
  polylines = [],
  infoWindows = [],
  clustered = false,
  children,
  ...googleMapProps
}: MapProps) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["geometry", "places"],
  });

  const renderMarker = (marker: MarkerData, index: number, clusterer?: any) => (
    <MarkerWithInfoWindow key={index} marker={marker} clusterer={clusterer} />
  );

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={{ ...containerStyle, ...customContainerStyle }}
      zoom={10}
      center={{
        lat: 39.90544474495378,
        lng: -82.91304185748207,
      }}
      options={{
        styles: mapStyles,
      }}
      onLoad={onLoad}
      {...googleMapProps}
    >
      {clustered ? (
        <MarkerClustererF>
          {(clusterer) => (
            <>{markers.map((marker, index) => renderMarker(marker, index, clusterer))}</>
          )}
        </MarkerClustererF>
      ) : (
        markers.map((marker, index) => renderMarker(marker, index))
      )}

      {polylines.map((polyline, index) => {
        const path =
          polyline.path ||
          (polyline.polylineString && window.google?.maps?.geometry?.encoding
            ? window.google.maps.geometry.encoding.decodePath(
                polyline.polylineString
              )
            : []);
        return <PolylineF key={index} path={path} options={polyline.options} />;
      })}

      {infoWindows.map((infoWindow, index) => (
        <InfoWindowF
          key={index}
          position={infoWindow.position}
          onCloseClick={infoWindow.onCloseClick}
        >
          <Box>{infoWindow.content}</Box>
        </InfoWindowF>
      ))}

      {children}
    </GoogleMap>
  ) : null;
};

export const createMarker = (
  position: google.maps.LatLng | google.maps.LatLngLiteral,
  options?: google.maps.MarkerOptions,
  onClick?: () => void,
  infoWindow?: {
    content: ReactNode;
    extensionContent?: InfoWindowContent;
    onCloseClick?: () => void;
    position?: google.maps.LatLng | google.maps.LatLngLiteral;
  }
): MarkerData => ({
  position,
  options,
  onClick,
  ...(infoWindow && { infoWindow }),
});

export const createPolyline = (
  path: google.maps.LatLng[] | google.maps.LatLngLiteral[],
  options?: google.maps.PolylineOptions
): PolylineData => ({
  path,
  options,
});

export const createPolylineFromString = (
  polylineString: string,
  options?: google.maps.PolylineOptions
): PolylineData => ({
  polylineString,
  options,
});

export const createInfoWindow = (
  position: google.maps.LatLng | google.maps.LatLngLiteral,
  content: ReactNode,
  onCloseClick?: () => void,
  extensionContent?: InfoWindowContent
): InfoWindowData => ({
  position,
  content,
  extensionContent,
  onCloseClick,
});

export default Map;
